import { useMemo } from "react";

import { css } from "@compiled/react";
import { flattenValues } from "@uniformdev/canvas";
import { UniformSlot } from "@uniformdev/canvas-react";
import { FilterDefinition } from "src/uniform/contenttypes/filterDefinition";
import { SortDefinition } from "src/uniform/contenttypes/sortDefinition";
import { FilterableGalleryProvider } from "src/uniform/contexts/filterable-gallery-context";
import { FilterableItemProvider } from "src/uniform/contexts/filterable-item-context";

import UFOSegment from "@atlaskit/react-ufo/segment";
import { token } from "@atlaskit/tokens";

import {
    ColumnLayout,
    ColumnLayoutProps,
    wacToken
} from "@/libraries/design/foundations";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";

import { isContentfulEnvelope } from "../../../../clients/uniform-client/enhancers/contentful-includes-enhancer";

import { FILTER_DROPDOWN_DATA_ATTR } from "./components/filter-control";
import { Toolbar } from "./components/toolbar";
import { useFilterState } from "./hooks/use-filter-state";
import { useOutsideClick } from "./hooks/use-outside-click";
import {
    applyFilters,
    applySearch,
    applySort,
    buildLabelMaps
} from "./utils/filterEngine";

export interface UniformFilterableGalleryProps {
    /** Pre-unwrapped array of data items from a data resource. */
    readonly items?: unknown;
    /** Dot-paths for free-text search. Omit to hide the search bar. */
    readonly searchFieldPaths?: string[];
    /** Uniform Block param holding FilterDefinition children. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly filters?: any;
    /** Uniform Block param holding SortDefinition children. */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly sorts?: any;
    /** When true (default), filter/search/sort state is mirrored to the URL. */
    readonly urlSync?: boolean;
    /** Column layout pattern (e.g. "4-4-4" for 3 columns, "3-3-3-3" for 4). */
    readonly layout?: ColumnLayoutProps["layout"];
    /**
     * Uniform Block param holding FacetDataSource children. Each entry has a
     * `sourceId` (matching a FilterDefinition ID) and a `data` field bound to a
     * separate data resource (e.g. Query - Template Categories at $.items).
     * Unwrapped via flattenValues into Record<string, unknown[]> at runtime.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly facetDataSources?: any;
}

const UniformFilterableGallery = ({
    items: rawItems,
    searchFieldPaths: rawSearchFieldPaths,
    filters: rawFilters,
    sorts: rawSorts,
    urlSync = true,
    layout = "4-4-4",
    facetDataSources: rawFacetDataSources
}: UniformFilterableGalleryProps): JSX.Element => {
    const items = useMemo(() => {
        if (Array.isArray(rawItems)) return rawItems;
        if (isContentfulEnvelope(rawItems)) return rawItems.items;
        return [];
    }, [rawItems]);
    const searchFieldPaths = useMemo(
        () =>
            Array.isArray(rawSearchFieldPaths)
                ? rawSearchFieldPaths.filter(
                      (path) => typeof path === "string" && path
                  )
                : [],
        [rawSearchFieldPaths]
    );
    const filterDefs = useMemo<FilterDefinition[]>(() => {
        if (!rawFilters) return [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const flat = flattenValues(rawFilters as any) as FilterDefinition[];
        return Array.isArray(flat) ? flat : [];
    }, [rawFilters]);
    const sortDefs = useMemo<SortDefinition[]>(() => {
        if (!rawSorts) return [];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const flat = flattenValues(rawSorts as any) as SortDefinition[];
        return Array.isArray(flat) ? flat : [];
    }, [rawSorts]);

    // Unwrap the facetDataSources content type block into a Record<sourceId, data[]>.
    // Content type block fields (unlike $block params) resolve data bindings directly into
    // `fields[id].value`, so both sourceId and data are readable via entry.fields[id].value.
    const optionsDataSources = useMemo<Record<string, unknown[]>>(() => {
        if (!rawFacetDataSources) return {};
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        // Uniform resolves content type block fields into fields[id].value directly,
        // so both paths are checked: the prop may arrive as a flat array (content type
        // block) or wrapped in a { value: [...] } envelope ($block param style).
        const rawEntries: unknown[] = Array.isArray(rawFacetDataSources)
            ? rawFacetDataSources
            : // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (rawFacetDataSources as any)?.value ?? [];
        if (!Array.isArray(rawEntries) || rawEntries.length === 0) return {};

        return Object.fromEntries(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            rawEntries.flatMap((entry: any) => {
                const sourceId = entry?.fields?.sourceId?.value;
                const data = entry?.fields?.data?.value;
                if (typeof sourceId !== "string" || !Array.isArray(data)) {
                    return [];
                }
                return [[sourceId, data]];
            })
        );
    }, [rawFacetDataSources]);

    const {
        filterObject,
        searchQuery,
        selectedSortId,
        dropdownVisibility,
        isAnyDropdownOpen,
        setSearchQuery,
        handleFilterButtonClick,
        toggleFilterOption,
        clearAllFilters,
        handleSearchSubmit,
        handleSearchClear,
        handleSortChange,
        closeAllDropdownsAndCommit
    } = useFilterState({ items, filterDefs, urlSync, optionsDataSources });

    const filteredItems = useMemo(
        () =>
            applySort(
                applySearch(
                    applyFilters(items, filterDefs, filterObject),
                    searchFieldPaths,
                    searchQuery
                ),
                sortDefs.find((sort) => sort.id === selectedSortId)
            ),
        [
            items,
            filterDefs,
            filterObject,
            searchFieldPaths,
            searchQuery,
            sortDefs,
            selectedSortId
        ]
    );

    const labelMaps = useMemo(
        () => buildLabelMaps(items, filterDefs, optionsDataSources),
        [items, filterDefs, optionsDataSources]
    );

    useOutsideClick({
        isActive: isAnyDropdownOpen,
        insideMarkerAttribute: FILTER_DROPDOWN_DATA_ATTR,
        onOutsideClick: closeAllDropdownsAndCommit
    });

    if (!Array.isArray(rawItems) && !isContentfulEnvelope(rawItems)) {
        return <InvalidItemsMessage />;
    }

    return (
        <UFOSegment name="uniform-filters">
            <TypographyStyles>
                <Toolbar
                    filterDefs={filterDefs}
                    sortDefs={sortDefs}
                    searchFieldPaths={searchFieldPaths}
                    filterObject={filterObject}
                    searchQuery={searchQuery}
                    selectedSortId={selectedSortId}
                    dropdownVisibility={dropdownVisibility}
                    labelMaps={labelMaps}
                    onSearchChange={setSearchQuery}
                    onSearchSubmit={handleSearchSubmit}
                    onSearchClear={handleSearchClear}
                    onFilterButtonClick={handleFilterButtonClick}
                    onOptionToggle={toggleFilterOption}
                    onClearAllFilters={clearAllFilters}
                    onSortChange={handleSortChange}
                />

                <FilterableGalleryProvider
                    items={items}
                    filteredItems={filteredItems}
                >
                    <div css={gallerySlotStyle}>
                        {filteredItems.length === 0 ? (
                            <p
                                css={emptyStateStyle}
                                data-testid="filterable-gallery-empty"
                            >
                                No items match your filters.
                            </p>
                        ) : (
                            <ColumnLayout layout={layout} renderAsList="ul">
                                {filteredItems.map((item, i) => (
                                    <li
                                        key={extractKey(item, i)}
                                        data-testid="filterable-gallery-item"
                                    >
                                        <FilterableItemProvider item={item}>
                                            <UniformSlot name="card" />
                                        </FilterableItemProvider>
                                    </li>
                                ))}
                            </ColumnLayout>
                        )}
                    </div>
                </FilterableGalleryProvider>
            </TypographyStyles>
        </UFOSegment>
    );
};

/** Rendered when the `items` prop is not an array (misconfiguration in the editor). */
const InvalidItemsMessage = () => (
    <p css={invalidItemsMessageStyle}>
        filterableGallery: <code>items</code> param must be an array.
    </p>
);

const invalidItemsMessageStyle = css({
    color: wacToken("color.secondary.red"),
    fontFamily: "monospace"
});

const gallerySlotStyle = css({
    marginBlockStart: token("space.400")
});

const emptyStateStyle = css({
    padding: token("space.200"),
    color: token("color.text.subtlest"),
    fontStyle: "italic",
    textAlign: "center"
});

const extractKey = (item: unknown, fallback: number): string | number => {
    if (typeof item !== "object" || item === null) return fallback;
    const sys = (item as Record<string, unknown>)["sys"];
    if (typeof sys !== "object" || sys === null) return fallback;
    const id = (sys as Record<string, unknown>)["id"];
    return typeof id === "string" ? id : fallback;
};

export default UniformFilterableGallery;
